// Resolve the LIVE section instance id from the DOM. Section-group sections are
// rendered as `sections--<id>__<key>`, and the Section Rendering API only returns
// a section WITH its saved settings when fetched by this exact id. Fetching the
// bare type name (e.g. `cart-drawer`) renders the section file standalone with
// SCHEMA DEFAULTS — which silently drops per-instance settings on re-render.
function getCartDrawerSectionId() {
  return document.getElementById('CartDrawer')?.dataset?.id || 'cart-drawer';
}
function getHeaderSectionId() {
  return document.querySelector('[id$=header]')?.id.split('shopify-section-')[1] || 'header';
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && this.dataset.index) {
        try {
          cartItems.updateQuantity(this.dataset.index, 0);
        } catch (error) {
          console.error('Error removing cart item:', error);
        }
      } else {
        console.warn('Cart items element or index not found');
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  cartClear() {
    let section = this.getSectionsToRender().map((section) => section.section);

    let data = {
      sections: section,
      updates: {},
    };

    fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((response) => {
        setTimeout(() => {
          this.renderContents(response);
        }, 100);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  // Full re-render of this component's sections (cart page). The cart drawer
  // overrides this in cart-drawer.js with a partial/light strategy.
  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const scope = document.getElementById(section.id);
      if (!scope) return;
      const target = scope.querySelector(section.selector) || scope;
      const inner = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      if (inner !== null) target.innerHTML = inner;
    });
  }

  onChange(event) {
    if (!event.target.dataset.index || !event.target.value) {
      console.warn('Missing required data for cart update:', event.target.dataset);
      return;
    }

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement?.getAttribute('name') || '',
      event.target.dataset.quantityVariantId,
    );
  }

  // Cart page: re-fetch and replace the whole cart section. The drawer overrides
  // this in cart-drawer.js.
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => console.error(e));
  }

  // Cart page sections only. The cart drawer provides its own list (and partial
  // render strategy) in cart-drawer.js.
  getSectionsToRender() {
    return [
      {
        id: 'main-cart',
        section: document.getElementById('main-cart')?.dataset?.id || 'main-cart',
        selector: '.js-contents',
      },
      // The cart footer lives inside #main-cart's .js-contents, so it is
      // refreshed by the 'main-cart' entry above. No separate footer fetch
      // is needed (cart-footer is now a shared snippet, not its own section).
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();

    let errors;
    let isMaxQuantityError = false;

    // Handle both cart page and cart drawer line items
    const cartLineItem = document.getElementById(`CartItem-${line}`);
    const cartDrawerLineItem = document.getElementById(`CartDrawer-Item-${line}`);
    const lineItem = cartLineItem || cartDrawerLineItem;

    // Handle both cart page and cart drawer quantity elements
    const cartQuantityElement = document.getElementById(`Quantity-${line}`);
    const cartDrawerQuantityElement = document.getElementById(`Drawer-quantity-${line}`);
    const quantityElement = cartQuantityElement || cartDrawerQuantityElement;

    const body = JSON.stringify({
      line,
      quantity: parseInt(quantity),
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            console.error('Cart update error response:', text);
            throw new Error(`HTTP error! status: ${response.status} - ${text}`);
          });
        }
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          errors = parsedState.errors;
          // Reset quantity values for both cart page and cart drawer
          if (cartQuantityElement) {
            cartQuantityElement.value = cartQuantityElement.getAttribute('value');
          }
          if (cartDrawerQuantityElement) {
            cartDrawerQuantityElement.value = cartDrawerQuantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Filter out service items from parsedState.items
        const nonServiceItems = parsedState.items.filter((item) => item.product_type !== 'Extra services');

        this.classList.toggle('is-empty', nonServiceItems.length === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', nonServiceItems.length === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', nonServiceItems.length === 0);

        if (nonServiceItems.length === 0) {
          this.cartClear();
        }

        // Re-render this component's sections. Polymorphic: the cart page does a
        // full re-render (base), the drawer does a partial/light update (override).
        this.renderContents(parsedState);

        if (window.updateWishlistStateImmediately) {
          window.updateWishlistStateImmediately();
        }

        let message = '';
        if (quantity > 0 && parsedState.items && parsedState.items.length > 0) {
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          if (
            items.length === parsedState.items.length &&
            updatedValue !== undefined &&
            quantityElement &&
            parseInt(quantityElement.value) !== updatedValue
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings?.error || 'Error updating quantity';
            } else {
              message =
                window.cartStrings?.quantityError?.replace('[quantity]', updatedValue) ||
                `Quantity updated to ${updatedValue}`;
            }
          }
        }

        this.updateLiveRegions(line, message);

        // Focus moves, not layers: the row being edited has just been replaced, so
        // focus goes onto its replacement. The drawer, when open, is already a layer.
        // Pushing one here would replace it and take Escape away from the drawer.
        const renderedLineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        const focusElement = renderedLineItem?.querySelector(`[name="${name}"]`);
        if (focusElement) {
          if ((cartDrawerWrapper && cartDrawerLineItem) || cartLineItem) {
            focusElement.focus({ preventScroll: true });
          }
        } else if (nonServiceItems.length === 0 && cartDrawerWrapper) {
          cartDrawerWrapper.querySelector('.js-drawer-inner-empty a')?.focus({ preventScroll: true });
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          document.querySelector('.js-cart-item-name')?.focus({ preventScroll: true });
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((e) => {
        if (quantity === 0) {
          this.onCartUpdate();
        }

        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));

        // Handle error display for both cart page and cart drawer
        const cartErrors = document.getElementById('CartPage-Errors');
        const cartDrawerErrors = document.getElementById('CartDrawer-Errors');
        const errors = cartErrors || cartDrawerErrors;

        const maxQuantity = quantityElement?.getAttribute('max') || quantityElement?.dataset.cartQuantity;

        if (maxQuantity && parseInt(quantity) > parseInt(maxQuantity)) {
          isMaxQuantityError = true;
          const errorMessage = window.cartStrings?.quantityErrorAlert?.replace('[quantity]', quantity);
          if (errorMessage) {
            this.updateLiveRegions(line, errorMessage);
          }
          // Reset quantity values for both cart page and cart drawer
          if (cartQuantityElement) {
            cartQuantityElement.value = cartQuantityElement.getAttribute('value');
          }
          if (cartDrawerQuantityElement) {
            cartDrawerQuantityElement.value = cartDrawerQuantityElement.getAttribute('value');
          }
        } else if (errors) {
          errors.textContent = window.cartStrings?.error || 'Error updating cart';
        }
      })
      .finally(() => {
        this.disableLoading(line);

        if (!this.classList.contains('main-cart-items')) {
          return;
        }

        if (this.classList.contains('is-empty')) {
          return;
        }

        if (isMaxQuantityError) {
          return;
        }

        let templateName;
        let replacements;

        if (errors) {
          templateName = 'message';
          replacements = { message: errors };
        } else if (quantity > 0 && lineItem?.dataset.lineTitle != undefined) {
          templateName = 'item-quantity-changed';
          replacements = { title: lineItem?.dataset.lineTitle, value: quantity };
        } else {
          templateName = 'item-removed';
          replacements = { title: lineItem?.dataset?.lineTitle ?? '', value: quantity };
        }

        // The low-order-value service fee line is the one case where removal is
        // good news — force success. Everything else keeps the severity baked
        // into its template (item-quantity-changed: success, item-removed:
        // warning, message: alert).
        const tagToFind = window.extraServices?.low_order_value_service_fee_product;
        const link = lineItem?.querySelector('a');
        const isServiceFeeLine = !!(tagToFind && link?.href?.includes(tagToFind));
        const notificationType = isServiceFeeLine ? 'success' : null;

        if (typeof window.showCartNotification === 'function') {
          // duration: 0 — cart page notifications persist (historical behavior)
          window.showCartNotification(templateName, replacements, { type: notificationType, duration: 0 });
        }
      });
  }

  updateLiveRegions(line, message) {
    if (!message) return;

    const phoneValidationErrorMessage = this.decodeHtmlEntities(
      window.cartStrings?.validation?.phone?.errorMessage || '',
    );
    let isPhoneValidation = false;

    if (phoneValidationErrorMessage) {
      const regex = /\(([^)]+)\)/g;
      const messageHasPhone = message.match(regex);
      if (messageHasPhone) {
        const messagePhoneTranslation = phoneValidationErrorMessage.replace('[[phone]]', messageHasPhone[0]);
        if (message === messagePhoneTranslation) {
          isPhoneValidation = true;
        }
      }
    }

    // Handle line item errors for both cart page and cart drawer
    const lineItemError = document.getElementById(`Line-item-error-${line}`);
    const cartDrawerLineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);

    if ((lineItemError || cartDrawerLineItemError) && !isPhoneValidation) {
      // Update cart page error if it exists
      if (lineItemError) {
        const errorTextElement = lineItemError.querySelector('.cart-item__error-text');
        if (errorTextElement) {
          errorTextElement.innerHTML = message;
        }
      }

      // Update cart drawer error if it exists
      if (cartDrawerLineItemError) {
        const cartDrawerErrorTextElement = cartDrawerLineItemError.querySelector('.cart-item__error-text');
        if (cartDrawerErrorTextElement) {
          cartDrawerErrorTextElement.innerHTML = message;
        }
      }
    }

    // Handle line item status elements for both cart page and cart drawer
    const cartLineItemStatus = document.getElementById('shopping-cart-line-item-status');
    const cartDrawerLineItemStatus = document.getElementById('CartDrawer-LineItemStatus');

    if (cartLineItemStatus) {
      cartLineItemStatus.setAttribute('aria-hidden', true);
    }
    if (cartDrawerLineItemStatus) {
      cartDrawerLineItemStatus.setAttribute('aria-hidden', true);
    }

    // Handle live region text for both cart page and cart drawer
    const cartLiveRegionText = document.getElementById('cart-live-region-text');
    const cartDrawerLiveRegionText = document.getElementById('CartDrawer-LiveRegionText');

    // Update both live region texts if they exist
    if (cartLiveRegionText) {
      cartLiveRegionText.setAttribute('aria-hidden', false);
      cartLiveRegionText.textContent = message;

      setTimeout(() => {
        cartLiveRegionText.setAttribute('aria-hidden', true);
      }, 1000);
    }

    if (cartDrawerLiveRegionText) {
      cartDrawerLiveRegionText.setAttribute('aria-hidden', false);
      cartDrawerLiveRegionText.textContent = message;

      setTimeout(() => {
        cartDrawerLiveRegionText.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  // Null-safe: returns the inner HTML of `selector` within `html`, or null if
  // either the section HTML or the selector is missing. Callers skip the update
  // when null, so a section that conditionally omits an element never throws.
  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : null;
  }

  enableLoading(line) {
    // Handle both cart page and cart drawer containers
    const mainCartItems = document.getElementById('main-cart-items');
    const cartDrawerItems = document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }
    if (cartDrawerItems) {
      cartDrawerItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();

    // Handle line item status for both cart page and cart drawer
    const cartLineItemStatus = document.getElementById('shopping-cart-line-item-status');
    const cartDrawerLineItemStatus = document.getElementById('CartDrawer-LineItemStatus');

    if (cartLineItemStatus) {
      cartLineItemStatus.setAttribute('aria-hidden', false);
    }
    if (cartDrawerLineItemStatus) {
      cartDrawerLineItemStatus.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    // Handle both cart page and cart drawer containers
    const mainCartItems = document.getElementById('main-cart-items');
    const cartDrawerItems = document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }
    if (cartDrawerItems) {
      cartDrawerItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  decodeHtmlEntities(text) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.documentElement.textContent;
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
